import { useEffect, useRef } from "react";
import {
  useWebSocket,
  type WsEventEnvelope,
  type SubscribeStatus,
} from "./web-socket-context";

interface UseWsChannelOptions {
  resType: string;
  resId: string | null;
  onEvent: (event: WsEventEnvelope) => void;
}

/**
 * Subscribe to a WebSocket entity channel.
 *
 * - Mounts subscribe / unmounts unsubscribe automatically
 * - `onEvent` is ref-stable (no re-subscribe on callback change)
 * - Once denied, does not auto-retry
 */
export function useWsChannel({
  resType,
  resId,
  onEvent,
}: UseWsChannelOptions): { status: SubscribeStatus; connected: boolean } {
  const { subscribe, unsubscribe, connected } = useWebSocket();
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const statusRef = useRef<SubscribeStatus>("pending");
  const deniedRef = useRef(false);

  useEffect(() => {
    if (!resId || !connected || deniedRef.current) return;

    const handler = (event: WsEventEnvelope) => {
      onEventRef.current(event);
    };

    subscribe({ resType, resId, handler });
    return () => {
      unsubscribe({ resType, resId, handler });
    };
  }, [resType, resId, connected, subscribe, unsubscribe]);

  return { status: statusRef.current, connected };
}
